"use client";

import { TypePackageDetailWithCarousal } from "@contentful/types";
import { getYzsaId } from "@yardzen/next-client-util";
import { Button } from "@yardzen/ui/components/button";
import { Link } from "@yardzen/ui/components/link";
import { P } from "@yardzen/ui/components/p";
import { Select, SelectItem } from "@yardzen/ui/components/select";
import { throttle } from "lodash";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useBuilderPartnerCheckoutHref } from "./builderPartnerCheckoutHref";

export function PackageDetailBanner({
  packageDetailBanner,
  lotSize,
  splitTreatment,
}: {
  packageDetailBanner: TypePackageDetailWithCarousal<"WITHOUT_UNRESOLVABLE_LINKS">;
  lotSize?: string;
  splitTreatment?: boolean;
}) {
  const [hostName, setHostName] = useState<string>("");
  const withBuilderPartnerParams = useBuilderPartnerCheckoutHref();

  useEffect(() => {
    setHostName(window.location.origin);
  }, []);

  const { modificationOptions, uniqueIdentifier } =
    packageDetailBanner?.fields.package?.fields || {};

  const [currentModificationOption, setCurrentModificationOption] = useState(
    modificationOptions ? modificationOptions[0] : null,
  );
  const [active, setActive] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const bannerOffsetRef = useRef(0);

  const currentDate = new Date();
  currentDate.setDate(currentDate.getDate() + 7);

  const chooseModificationOption = (e: ChangeEvent<HTMLSelectElement>) => {
    const chosenOption = modificationOptions?.find(
      (option) => option?.fields.internalValue === e?.target?.value,
    );
    // if for some reason the select value wasn't valid and the chosen option wasn't found, just ignore
    if (chosenOption) {
      setCurrentModificationOption(chosenOption);
    }
  };

  useEffect(() => {
    // Figure out the trigger point height from top.
    const navElement = document.querySelector("#sub-nav-bar-trigger");
    let currentNavHeight = 0;

    if (navElement !== null && navElement instanceof HTMLElement) {
      const bound = navElement.getBoundingClientRect();
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      currentNavHeight = scrollTop + bound.top;
    }

    // Throttle the scroll events.
    const scrollHandler = throttle((navHeight: number) => {
      // If user scroll position is past the trigger point show nav
      if (window.scrollY < navHeight) {
        setActive(false);
      } else {
        setActive(true);
      }
    }, 250);

    window.addEventListener(
      "scroll",
      scrollHandler.bind(null, currentNavHeight),
    );
    return () => {
      window.removeEventListener(
        "scroll",
        scrollHandler.bind(null, currentNavHeight),
      );
    };
  }, [setActive]);

  useEffect(() => {
    //This checks for the presence of the promo banner while the user is scrolling
    //since the announcement banner loads at different times
    let lastScrollY = window.scrollY;

    const scrollHandler = throttle(() => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY || currentScrollY < lastScrollY) {
        const promoBannerElement = document.querySelector("#yz-banner");
        if (promoBannerElement) {
          bannerOffsetRef.current = promoBannerElement.clientHeight;
        }
      }

      lastScrollY = currentScrollY;
    }, 250);

    window.addEventListener("scroll", scrollHandler);
    return () => {
      window.removeEventListener("scroll", scrollHandler);
    };
  }, []);

  useEffect(() => {
    // Function to check if the screen is small
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768); // You can adjust the breakpoint as needed
    };
    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);
    return () => {
      window.removeEventListener("resize", checkIsMobile);
    };
  }, []);

  const challengerUrl =
    currentModificationOption?.fields.cta?.fields.challengerURL ??
    packageDetailBanner.fields.package?.fields.cta?.fields.challengerURL ??
    "";
  const controlUrl =
    currentModificationOption?.fields.cta?.fields.url ??
    packageDetailBanner.fields.package?.fields.cta?.fields.url ??
    "";

  // If split treatment is on and challenger URL is defined, use the challenger URL.
  const fallbackUrl = new URL("https://yardzen.com");
  let ctaUrl: URL = fallbackUrl;

  try {
    const urlToUse =
      splitTreatment && challengerUrl ? challengerUrl : controlUrl;
    if (urlToUse && urlToUse.trim() !== "") {
      const baseUrl = hostName || "https://yardzen.com";
      ctaUrl = urlToUse.startsWith("http")
        ? new URL(urlToUse)
        : new URL(urlToUse, baseUrl);
    }
  } catch (error) {
    console.warn("Invalid URL provided for CTA:", {
      challengerUrl,
      controlUrl,
      hostName,
    });
    ctaUrl = fallbackUrl;
  }

  const yzsaCookie = getYzsaId();

  // Add yzsa parameter to url like wordpress was doing.
  if (ctaUrl && yzsaCookie && !ctaUrl.searchParams.has("yzsa")) {
    ctaUrl.searchParams.set("yzsa", encodeURIComponent(yzsaCookie));
  }

  // Checkout page requires extra packageDetail param
  if (ctaUrl && uniqueIdentifier && !ctaUrl.searchParams.has("packageDetail")) {
    ctaUrl.searchParams.set(
      "packageDetail",
      encodeURIComponent(uniqueIdentifier),
    );
  }

  return (
    <div id="sub-nav-bar-trigger">
      <nav
        id="sub-nav-bar"
        style={
          isMobile
            ? { bottom: "16px" }
            : { top: `${bannerOffsetRef.current + 80}px` }
        }
        className={`bg-system-grayscale-00 fixed left-0 right-0 z-50 mx-4 mt-3 justify-center rounded-lg bg-opacity-80 shadow-md backdrop-blur-[64px] md:mt-2 ${!active ? "hidden" : ""}`}
      >
        <div className="mx-auto flex flex-col justify-between px-6 py-2 md:flex-row md:py-4">
          <div className="hidden items-center md:flex">
            <P className="text-xl">
              {packageDetailBanner.fields.package?.fields.heading}
            </P>
          </div>

          <div className="flex items-center justify-end gap-2 max-sm:flex-col md:justify-center">
            {modificationOptions && (
              <Select
                defaultValue={lotSize}
                wrapperClassName="shrink-0"
                onChange={chooseModificationOption}
                className="border-system-grayscale-20 !w-full max-w-full cursor-pointer rounded-lg border bg-white pr-8 text-base font-normal focus:outline-none focus:ring-0"
              >
                {modificationOptions.map((modificationOption) => (
                  <SelectItem
                    className="hover:bg-brand-beige-lighter min-h-6 bg-white"
                    key={modificationOption?.fields.internalValue}
                    value={modificationOption?.fields.internalValue}
                  >
                    {`${modificationOption?.fields.heading} - $${modificationOption?.fields.promoPrice ?? modificationOption?.fields.price}`}
                  </SelectItem>
                ))}
              </Select>
            )}
            <Button className="mx-2 w-full" asChild>
              <Link
                className="m-0 whitespace-nowrap font-medium max-[429px]:py-3 max-[390px]:px-3 max-[365px]:whitespace-normal max-[365px]:px-6 max-[365px]:leading-3 min-[360px]:inline-flex"
                href={withBuilderPartnerParams(
                  hostName
                    ? ctaUrl.toString()
                    : ctaUrl.pathname + ctaUrl.search || "https://yardzen.com",
                )}
              >
                {packageDetailBanner.fields.package?.fields.cta?.fields
                  .buttonText || "Buy Now"}
              </Link>
            </Button>
          </div>
        </div>
      </nav>
    </div>
  );
}
